'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, LockKeyhole, Plus, Search, X } from 'lucide-react';
import { RoomHeader, RoomFooter } from '@/components/room-shell';
import { roomPresentation } from '@/lib/room-presentation';
import { roomPath } from '@/lib/seo';
import type { UserProfile } from '@/lib/types';

export interface Realm {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  type: 'PUBLIC' | 'PRIVATE' | 'CURATED';
  category: string | null;
  memberCount: number;
  _count?: { members: number };
}

export default function RealmsListPage({ initialRealms }: { initialRealms: Realm[] }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [realms, setRealms] = useState<Realm[]>(initialRealms);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    async function loadMemberships() {
      try {
        const response = await fetch('/api/auth/me', { signal: controller.signal });
        if (!response.ok) return;
        setUser(await response.json());
        const result = await fetch('/api/realms', { signal: controller.signal });
        if (result.ok) { const data = await result.json(); setRealms(data.realms || initialRealms); }
      } catch { /* The public directory remains available if account data cannot load. */ }
    }
    loadMemberships();
    return () => controller.abort();
  }, [initialRealms]);

  const query = searchQuery.trim().toLowerCase();
  const categories = Array.from(new Set(realms.map(room => room.category).filter((value): value is string => !!value)));
  const filtered = realms.filter(room => (!category || room.category === category) && (!query || [room.name, room.description, room.category].some(text => text?.toLowerCase().includes(query))));
  const publicRooms = filtered.filter(room => room.type === 'PUBLIC');
  const restrictedRooms = filtered.filter(room => room.type !== 'PUBLIC');
  const canCreate = user?.role === 'ADMIN' || user?.role === 'COMMUNITY_MANAGER';
  const reset = () => { setSearchQuery(''); setCategory(''); };

  return <div className="rooms-experience">
    <RoomHeader signedIn={!!user} />
    <main className="room-shell room-directory">
      <section className="room-directory-hero">
        <div><h1>Rooms</h1><p className="room-intro">Find people to talk to about a shared interest.</p></div>
        {canCreate && <Link href="/create-realm" className="room-outline-action"><Plus size={17} aria-hidden="true" />Create a room</Link>}
      </section>

      <section aria-label="Find rooms" className="room-discovery-tools">
        <div className="room-search"><Search size={20} aria-hidden="true" /><input type="search" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} aria-label="Search rooms" placeholder="Search rooms or topics" />{searchQuery && <button type="button" aria-label="Clear search" onClick={() => setSearchQuery('')}><X size={17} aria-hidden="true" /></button>}</div>
        {categories.length > 0 && <div className="room-category-filters" aria-label="Filter by topic"><button type="button" aria-pressed={!category} onClick={() => setCategory('')}>All topics</button>{categories.map(item => <button key={item} type="button" aria-pressed={category === item} onClick={() => setCategory(item)}>{item}</button>)}</div>}
      </section>
      <p className="room-results-count" role="status">{filtered.length} {filtered.length === 1 ? 'room' : 'rooms'}{query || category ? ' found' : ' available'}</p>
      {publicRooms.length > 0 && <section aria-labelledby="public-rooms"><div className="room-list-heading"><h2 id="public-rooms">Public rooms</h2></div><div className="room-directory-grid">{publicRooms.map(room => <RoomCard key={room.id} room={room} />)}</div></section>}
      {restrictedRooms.length > 0 && <section className="room-private-list" aria-labelledby="your-rooms"><div className="room-list-heading"><h2 id="your-rooms">Your private rooms</h2><LockKeyhole size={16} aria-hidden="true" /></div><div className="room-directory-grid">{restrictedRooms.map(room => <RoomCard key={room.id} room={room} />)}</div></section>}
      {filtered.length === 0 && <div className="room-empty-state"><Search size={32} aria-hidden="true" /><h2>{query || category ? 'No matching rooms' : 'No rooms available'}</h2><p>{query || category ? 'Try a different topic or clear your filters to see all rooms.' : 'Check back for new rooms, or create one for your community.'}</p>{query || category ? <button type="button" className="room-outline-action" onClick={reset}>Clear filters<ArrowRight size={16} aria-hidden="true" /></button> : canCreate && <Link href="/create-realm" className="room-outline-action">Create a room<Plus size={16} aria-hidden="true" /></Link>}</div>}

    </main>
    <RoomFooter />
  </div>;
}

function RoomCard({ room }: { room: Realm }) {
  const view = roomPresentation(room);
  const count = room._count?.members ?? room.memberCount;
  return <Link href={roomPath(room.slug)} className="room-directory-card" aria-label={`Explore ${room.name}`}>
    <span className="room-card-emblem" aria-hidden="true">{view.emoji}</span>
    <div className="room-card-content"><p className="room-eyebrow">{view.label}</p><h3>{room.name}</h3><p className="room-card-description">{view.summary}</p>
    {view.sponsored && <p className="room-card-sponsor">Sponsored by OneFiler</p>}</div>
    <div className="room-card-bottom"><span className="room-access-badge">{room.type === 'PUBLIC' ? 'Public' : 'Members only'}</span><span>{count} {count === 1 ? 'member' : 'members'}</span><strong>View room<ArrowRight size={16} aria-hidden="true" /></strong></div>
  </Link>;
}
